import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormControl as FormControl, UntypedFormGroup as FormGroup, Validators } from '@angular/forms';
import { TranslationService } from '@sdbn/common';
import { Visit, VisitorRoleReference, VisitTask } from '../../../../tmc-visit.model';
import { DateTime } from 'luxon';
import { ConfigurationService, HttpService } from '@sdbn/appclient';
import { environment } from 'src/environments/environment';
import { DatePipe } from '@angular/common';
import { PersonalVisitStatus } from 'src/tmc-visit/widgets/personal-visit-list/personal-visit-list.model';

@Component({
  selector: 'app-visit-masterdata',
  templateUrl: './visit-masterdata.component.html',
  styleUrls: ['./visit-masterdata.component.scss']
})
export class VisitMasterdataComponent implements OnInit {

  visit: Visit;
  inEditMode: boolean;

  idNumberRequired = false;


  types = new Array<VisitorRoleReference>();
  @Input('types') set setTypes(value: VisitorRoleReference[]) {
    this.types = value;
    if (this.visit != null) {
      this.patchVisitToForm(this.visit);
      this.applyFormListeners();
      this.fireInitialValidityChanged();
    }
  }

  registerArrival: boolean;
  @Input('registerArrival') set setRegisterArrival(value: boolean) {
    this.registerArrival = value;
    let idNumberRequired = false;
    if (value) {
      if (this.configurationService.isEnabled('tmc-visit.feature.idnumber-required', false)) {
        this.formGroup.controls['personalId'].addValidators(Validators.required);
        idNumberRequired = true;
      }
      this.formGroup.controls['confirmed'].addValidators(Validators.required);
    } else {
      this.formGroup.controls['personalId'].clearValidators();
      this.formGroup.controls['confirmed'].clearValidators();
    }
    this.idNumberRequired = idNumberRequired;
  }

  @Output() masterdataValidityChanged = new EventEmitter<boolean>();

  @Output() openMandatoryTasksChanged = new EventEmitter<boolean>();

  @Input() upfrontRegistration: boolean;

  @Input('visit') set setVisit(value: Visit) {
    this.visit = value;
  }

  @Input('inEditMode') set setInEditMode(inEditMode: boolean) {
    this.inEditMode = inEditMode;
  }

  formGroup = new FormGroup({
    'firstName': new FormControl(null, [Validators.required]),
    'name': new FormControl(null, [Validators.required]),
    'company': new FormControl(null, []),
    'visitorType': new FormControl(null, [Validators.required]),
    'confirmed': new FormControl(null, []),
    'sendVisitorMail': new FormControl(null, []),
    'visitorMail': new FormControl(null, []),

    'withCar': new FormControl(null, []),
    'numberplate': new FormControl(null, []),
    'inviteAdditionalVisitorsAllowed': new FormControl(null, []),
    'needsAreaAccessByCar': new FormControl(null, []),
    'grandAreaAccessByCar': new FormControl(null, []),
    'grandAreaAccessOnFoot': new FormControl(null, []),
    'plannedArrival': new FormControl(null, []),
    'defineEndDate': new FormControl(null, []),
    'definedEndDate': new FormControl(null, []),
    'visitEnd': new FormControl(null, []),
    'invitationRequiresConfirmation': new FormControl(null, []),
    'invitationConfirmationOnAccessOnly': new FormControl(null, []),
    'assignPrivilegesOnInvitation': new FormControl(null, []),
    'personalId': new FormControl(null, [])
  });

  patching = false;

  private datePipe = new DatePipe('de');

  constructor(
    public translationService: TranslationService,
    private tmcHttpService: HttpService,
    private configurationService: ConfigurationService
  ) {
    this.formGroup.controls.needsAreaAccessByCar.disable();
    this.formGroup.controls.definedEndDate.disable();
    
    this.formGroup.controls.invitationConfirmationOnAccessOnly.disable();
    this.formGroup.controls.invitationRequiresConfirmation.disable();
    this.formGroup.controls.assignPrivilegesOnInvitation.disable();
  }

  ngOnInit(): void {
    if (this.upfrontRegistration) {
      this.formGroup.controls['plannedArrival'].addValidators(Validators.required);
    } else {
      this.formGroup.removeControl('plannedArrival');
    }
    this.fireInitialValidityChanged();
  }

  private applyFormListeners() {
    this.formGroup.valueChanges.subscribe(values => {
      if (this.patching) {
        return;
      }
      var taskRefreshRequired = false;
      if (this.visit != null && this.types != null && this.types.length > 0) {
        const selectedVisitorType = values.visitorType != null ? this.types.find((t) => t.id === values.visitorType?.id) : null;

        taskRefreshRequired = //
          (this.visit.visitorType !== values.visitorType?.id) || //
          (this.visit.firstname !== values.firstName) || //
          (this.visit.name !== values.name) || //
          (this.visit.company !== values.company) || //
          (this.visit.visitorMail !== values.visitorMail);

        this.visit.confirmed = values.confirmed;
        this.visit.firstname = values.firstName;
        this.visit.name = values.name;
        this.visit.company = values.company;
        this.visit.sendVisitorMail = values.sendVisitorMail;
        this.visit.visitorMail = values.visitorMail;
        this.visit.withCar = values.withCar;
        this.visit.numberPlate = values.numberplate;
        this.visit.visitorType = values.visitorType?.id ?? null;
        this.visit.inviteAdditionalVisitorsAllowed = values.inviteAdditionalVisitorsAllowed;
        this.visit.grandAreaAccessByCar = values.grandAreaAccessByCar;
        this.visit.needsAreaAccessByCar = values.needsAreaAccessByCar;
        if(this.visit.allowOverrideRoleSettings) {
          this.visit.invitationRequiresConfirmation = values.invitationRequiresConfirmation;
          this.visit.invitationConfirmationOnAccessOnly = values.invitationConfirmationOnAccessOnly;
          this.visit.assignPrivilegesOnInvitation = values.assignPrivilegesOnInvitation;
        }
        this.visit.personalId = values.personalId;
        if (this.visit.grandAreaAccessByCar !== this.formGroup.controls.needsAreaAccessByCar.value) {
          this.formGroup.controls.needsAreaAccessByCar.setValue(this.visit.grandAreaAccessByCar, { emitEvent: false });
          this.visit.needsAreaAccessByCar = this.visit.grandAreaAccessByCar;
        }
        this.visit.grandAreaAccessOnFoot = values.grandAreaAccessOnFoot;

        this.visit.defineVisitEnd = values.defineEndDate;

        if (values.definedEndDate) {
          this.visit.definedVisitEnd = DateTime.fromJSDate(values.definedEndDate).toFormat('yyyy-MM-dd');
        }


        if (this.upfrontRegistration) {
          this.visit.plannedArrival = values.plannedArrival;
        }
        if (selectedVisitorType != null) {
          if (this.visit.plannedArrival !== undefined && this.visit.plannedArrival != null && selectedVisitorType.validDaysCount != null && this.visit.plannedArrival !== '') {
            this.visit.endByRole = DateTime.fromFormat(this.datePipe.transform(new Date(this.visit.plannedArrival), 'dd.MM.yyyy'), 'dd.MM.yyyy').plus({ days: selectedVisitorType.validDaysCount }).toFormat('yyyy-MM-dd');
          } else {
            this.visit.endByRole = DateTime.now().plus({ days: selectedVisitorType.validDaysCount }).toFormat('yyyy-MM-dd');
          }
        }

      }
      if (taskRefreshRequired && values.visitorType != null) {
        this.refreshTasks();
      }
    });

    this.formGroup.controls.defineEndDate.valueChanges.subscribe((v) => {
      if (v) {
        this.formGroup.controls.definedEndDate.enable({ emitEvent: false });
      } else {
        this.formGroup.controls.definedEndDate.disable({ emitEvent: false });
      }
    });

    this.formGroup.controls.withCar.valueChanges.subscribe(v => {
      if (v) {
        this.formGroup.controls.numberplate.enable({ emitEvent: false });
      } else {
        this.formGroup.controls.numberplate.disable({ emitEvent: false });
      }
    });

    this.formGroup.controls.visitorType.valueChanges.subscribe((visitorType) => {
      const selectedVisitorType = visitorType != null ? this.types.find((t) => t.id === visitorType?.id) : null;
      if (selectedVisitorType != null) {
        this.visit.grandAreaAccessByCar = selectedVisitorType.allowYardEntranceByCar;
        this.visit.grandAreaAccessOnFoot = selectedVisitorType.allowYardEntranceOnFoot;
        this.visit.needsAreaAccessByCar = this.visit.grandAreaAccessByCar === true;

        this.visit.allowOverrideRoleSettings = selectedVisitorType.allowOverrideRoleSettings === true;
        if(this.visit.allowOverrideRoleSettings) {
          this.formGroup.controls.invitationConfirmationOnAccessOnly.enable();
          this.formGroup.controls.invitationRequiresConfirmation.enable();
          this.formGroup.controls.assignPrivilegesOnInvitation.enable();
        } else {
          this.formGroup.controls.invitationConfirmationOnAccessOnly.disable();
          this.formGroup.controls.invitationRequiresConfirmation.disable();
          this.formGroup.controls.assignPrivilegesOnInvitation.disable();
        }

        this.visit.invitationRequiresConfirmation = selectedVisitorType.invitationRequiresConfirmation === true;
        this.visit.invitationConfirmationOnAccessOnly = selectedVisitorType.invitationConfirmationOnAccessOnly === true;
        this.visit.assignPrivilegesOnInvitation = selectedVisitorType.assignPrivilegesOnInvitation === true;

        this.formGroup.patchValue({
          grandAreaAccessByCar: selectedVisitorType.allowYardEntranceByCar === true,
          grandAreaAccessOnFoot: selectedVisitorType.allowYardEntranceOnFoot === true,
          needsAreaAccessByCar: this.visit.needsAreaAccessByCar,

          invitationRequiresConfirmation: this.visit.invitationRequiresConfirmation,
          invitationConfirmationOnAccessOnly: this.visit.invitationConfirmationOnAccessOnly,
          assignPrivilegesOnInvitation: this.visit.assignPrivilegesOnInvitation

        }, { emitEvent: false });
      }

    });

    this.formGroup.statusChanges.subscribe(() => {
      this.masterdataValidityChanged.next(this.formGroup.valid);
    });

  }

  private refreshTasks() {

    return this.tmcHttpService.postWithContext<VisitTask[]>(environment.personalVisitEndpoint + '/update-tasks', { tasks: this.visit.tasks, visitorRoleId: this.visit.visitorType, firstName: this.visit.firstname, name: this.visit.name, company: this.visit.company, mail: this.visit.visitorMail }).subscribe(
      response => {
        console.log('response', response);
        var tasks = new Array<VisitTask>();
        var openMandatoryTasks = false;
        if (!this.upfrontRegistration) {
          for (const task of response) {
            var t = new VisitTask(task);
            tasks.push(t);
            if (!t.done && !t.optional && !t.doneBefore) {
              openMandatoryTasks = true;
            }
          }
        }
        this.visit.tasks = tasks;
        this.openMandatoryTasksChanged.next(openMandatoryTasks);
      }
    );

  }

  private patchVisitToForm(visit: Visit) {
    if (this.formGroup != null && visit != null) {
      this.patching = true;

    if(this.visit.personalVisitStatus === PersonalVisitStatus.IN_PROCESS) {
      if (this.configurationService.isEnabled('tmc-visit.feature.idnumber-required', false)) {
        this.formGroup.controls['personalId'].addValidators(Validators.required);
        this.idNumberRequired = true;
      } else {
        this.formGroup.controls['personalId'].clearValidators();
      }
    }

      if (visit.defineVisitEnd) {
        this.formGroup.controls.definedEndDate.enable({ emitEvent: false });
      }
      
      if(this.visit.allowOverrideRoleSettings) {
        this.formGroup.controls.invitationConfirmationOnAccessOnly.enable();
        this.formGroup.controls.invitationRequiresConfirmation.enable();
        this.formGroup.controls.assignPrivilegesOnInvitation.enable();
      } else {
        this.formGroup.controls.invitationConfirmationOnAccessOnly.disable();
        this.formGroup.controls.invitationRequiresConfirmation.disable();
        this.formGroup.controls.assignPrivilegesOnInvitation.disable();
      }
      
      this.formGroup.patchValue({
        visitorType: visit.visitorType != null && this.types != null ? this.types.find(t => t.id === visit.visitorType) : null,
        firstName: visit.firstname,
        name: visit.name,
        company: visit.company,
        confirmed: visit.confirmed,
        sendVisitorMail: visit.sendVisitorMail,
        visitorMail: visit.visitorMail,
        withCar: visit.withCar,
        numberplate: visit.numberPlate,
        needsAreaAccessByCar: visit.needsAreaAccessByCar,
        grandAreaAccessByCar: visit.grandAreaAccessByCar,
        grandAreaAccessOnFoot: visit.grandAreaAccessOnFoot,
        plannedArrival: visit.plannedArrival,
        inviteAdditionalVisitorsAllowed: visit.inviteAdditionalVisitorsAllowed,
        defineEndDate: visit.defineVisitEnd,
        definedEndDate: this.getJSDate(visit.definedVisitEnd, 'yyyy-MM-dd'),
        visitEnd: visit.visitEnd,
        invitationRequiresConfirmation: visit.invitationRequiresConfirmation,
        invitationConfirmationOnAccessOnly: visit.invitationConfirmationOnAccessOnly,
        assignPrivilegesOnInvitation: visit.assignPrivilegesOnInvitation,
        personalId: visit.personalId
      }, { emitEvent: false });

      if (visit.withCar === true) {
        this.formGroup.controls.numberplate.enable({ emitEvent: false });
      } else {
        this.formGroup.controls.numberplate.disable({ emitEvent: false });
      }
    }
    this.patching = false;
  }

  fireInitialValidityChanged() {
    if (this.inEditMode != null && this.visit != null) {
      if (this.formGroup.dirty) {
        this.masterdataValidityChanged.next(this.formGroup.valid);
      }
    }
  }

  formatEndByRoleDate(endByRole: string) {
    return endByRole != null ? DateTime.fromFormat(endByRole, 'yyyy-MM-dd').toFormat('dd.MM.yyyy') : '';
  }

  private getJSDate(d: string, format: string) {
    let parsedDate = null;
    if (d != null && d.length > 0) {
      const p = DateTime.fromFormat(d, format);
      if (p.isValid) {
        parsedDate = new Date(p.year, p.month - 1, p.day);
      }
    }
    return parsedDate;
  }


}
